import { z } from 'zod';
import { ActorTypeSchema } from '../common/actor.schema';
import { BrokeragePageSnapshotContentSchema, ServedBuyerContractSchema } from './brokerage-agreement-page.schemas';

export const BROKERAGE_EMAIL_KINDS = [
  'NOTICE',
  'REMINDER',
  'CONFIRMATION',
  'WITHDRAWAL_RECEIPT',
  'ACCEPTANCE_REMINDER',
  'DATA_ROOM_RELEASE',
] as const;
export const BROKERAGE_EMAIL_STATUSES = ['PENDING', 'SENT', 'FAILED'] as const;
export const BROKERAGE_DELIVERY_STATUSES = ['DELIVERED', 'BOUNCED', 'COMPLAINED'] as const;

/** What the stored e-mail body carries where the personal link was. */
export const BROKERAGE_PERSONAL_LINK_PLACEHOLDER = '{personalLink}';

export const BrokerageProofEmailSchema = z.object({
  id: z.uuid(),
  kind: z.enum(BROKERAGE_EMAIL_KINDS),
  sender: z.string(),
  recipient: z.string(),
  replyTo: z.string().nullable(),
  subject: z.string(),
  bodyText: z.string(),
  status: z.enum(BROKERAGE_EMAIL_STATUSES),
  sentAt: z.iso.datetime().nullable(),
  failedAt: z.iso.datetime().nullable(),
  deliveryStatus: z.enum(BROKERAGE_DELIVERY_STATUSES).nullable(),
  deliveryStatusAt: z.iso.datetime().nullable(),
  fingerprint: z.string(),
  fingerprintMatches: z.boolean(),
});

export const BrokerageProofSchema = z.object({
  contract: z
    .object({
      concludedAt: z.iso.datetime(),
      contractNumber: z.string(),
      name: z.string(),
      // null: the served Muster had no read tick.
      noticeRead: z.boolean().nullable(),
      earlyStartRequested: z.boolean(),
      clientIp: z.string(),
      userAgent: z.string().nullable(),
      musterVersion: z.string(),
      // The Vorlage the contract was concluded under, from the concluded snapshot; derived as the standard for snapshots from before IMM-1431.
      template: ServedBuyerContractSchema,
      // null while the e-mail acceptance is outstanding.
      acceptanceReply: z
        .object({
          receivedAt: z.iso.datetime(),
          // null for a mark recorded before the marker was stored.
          markedBy: z
            .object({
              type: ActorTypeSchema,
              id: z.string(),
              // null when the name cannot be resolved (a removed operator, for instance).
              name: z.string().nullable(),
            })
            .nullable(),
        })
        .nullable(),
      snapshot: z.object({
        fingerprint: z.string(),
        fingerprintMatches: z.boolean(),
        firstServedAt: z.iso.datetime(),
        content: BrokeragePageSnapshotContentSchema,
        coverImageUrl: z.string().nullable(),
        contactPhotoUrl: z.string().nullable(),
        logoUrl: z.string().nullable(),
      }),
    })
    .nullable(),
  withdrawal: z
    .object({
      receivedAt: z.iso.datetime(),
      name: z.string(),
      contract: z.string(),
      email: z.string(),
      reason: z.string().nullable(),
      clientIp: z.string(),
      userAgent: z.string().nullable(),
    })
    .nullable(),
  emails: z.array(BrokerageProofEmailSchema),
});

export const GetBrokerageProofResponseSchema = BrokerageProofSchema;
export type BrokerageProof = z.infer<typeof BrokerageProofSchema>;
export type BrokerageProofEmail = z.infer<typeof BrokerageProofEmailSchema>;
export type BrokerageEmailKind = (typeof BROKERAGE_EMAIL_KINDS)[number];
